/**
 * Hook that deletes a line item from a contract extraction category.
 *
 * Calls the `deleteContractLineItem` mutation (SE-4382) which removes
 * the item at `(category, itemIndex)` from the extraction's
 * `extractedData` JSONB column and records an Activity audit row with
 * the deleted value. Callers are expected to gate this hook behind an
 * explicit confirmation dialog — the hook itself does not prompt the
 * user.
 * @remarks Refetches the `GetContractExtraction` query on success so
 * the list shrinks immediately in the review view. Consumed by
 * `ExtractionDataSectionContainer` in SE-4384.
 * @module features/contracts/hooks/useDeleteLineItem
 */

import { gql, useMutation } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { activeContractExtractionRefetch } from "./contractExtractionRefetch";

/** GraphQL document for the DeleteContractLineItem mutation. */
export const DELETE_CONTRACT_LINE_ITEM_MUTATION = gql`
  mutation DeleteContractLineItem($input: DeleteContractLineItemInput!) {
    deleteContractLineItem(input: $input) {
      success
    }
  }
`;

/** Shape of the DeleteContractLineItem mutation response. */
interface DeleteContractLineItemResponse {
  readonly deleteContractLineItem: { readonly success: boolean };
}

/** Shape of the DeleteContractLineItem mutation variables. */
interface DeleteContractLineItemVariables {
  readonly input: {
    readonly extractionId: string;
    readonly category: string;
    readonly itemIndex: number;
  };
}

/** Public API returned by `useDeleteLineItem`. */
export interface UseDeleteLineItemResult {
  /**
   * Deletes the line item at `(category, itemIndex)` from the
   * extraction data. On success, refetches the
   * `GetContractExtraction` query so the shrunken list renders
   * immediately.
   * @param extractionId - UUID of the extraction to update
   * @param category - Category key within `extractedData`
   * @param itemIndex - Zero-based index of the item within the category
   * @param playerId - Player UUID (needed for the extraction query refetch variables)
   * @param documentType - Document type (needed for the extraction query refetch variables)
   * @returns `true` when the server confirms success, `false` otherwise
   */
  readonly deleteLineItem: (
    extractionId: string,
    category: string,
    itemIndex: number,
    playerId: string,
    documentType: string
  ) => Promise<boolean>;
  /** Whether a delete request is currently in flight. */
  readonly isDeleting: boolean;
}

/**
 * Wraps the DeleteContractLineItem mutation with loading state and an
 * automatic refetch of the extraction query on success.
 * @returns Imperative delete function + loading state
 */
export function useDeleteLineItem(): UseDeleteLineItemResult {
  const [runMutation] = useMutation<
    DeleteContractLineItemResponse,
    DeleteContractLineItemVariables
  >(DELETE_CONTRACT_LINE_ITEM_MUTATION);

  const [isDeleting, setIsDeleting] = useState(false);

  const deleteLineItem = useCallback(
    async (
      extractionId: string,
      category: string,
      itemIndex: number,
      playerId: string,
      documentType: string
    ): Promise<boolean> => {
      setIsDeleting(true);
      try {
        const { data } = await runMutation({
          variables: {
            input: { extractionId, category, itemIndex },
          },
          refetchQueries: [
            activeContractExtractionRefetch(playerId, documentType),
          ],
          awaitRefetchQueries: true,
        });
        return data?.deleteContractLineItem?.success === true;
      } catch {
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [runMutation]
  );

  return useMemo(
    () => ({ deleteLineItem, isDeleting }),
    [deleteLineItem, isDeleting]
  );
}
